import React from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowUpRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHero, GoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { CONTACT } from "@/constants/testIds";
import { IMG } from "@/constants/images";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  { value: "Mobile Notary", label: "Mobile Notary" },
  { value: "Remote Online Notary", label: "Remote Online Notary (RON)" },
  { value: "Loan Signing Agent", label: "Loan Signing Agent" },
  { value: "Apostille", label: "Apostille / International Document" },
  { value: "Wedding Officiant", label: "Wedding Officiant / Ministerial Ceremony" },
  { value: "Elder Care Notary", label: "Elder Care / Bedside / Senior Services" },
  { value: "Cruise-Port Signings", label: "Cruise-Port Signings (Port Canaveral)" },
  { value: "Other", label: "Other / Not Sure" },
];

const SERVICE_VALUES = new Set(SERVICES.map((s) => s.value));

// Smart-hero variants — each specialty service gets a tailored eyebrow, title,
// lead paragraph, message placeholder, and backdrop when linked-to via
// /contact?service=<value>. Anything not in this map falls back to the default.
const HERO_VARIANTS = {
  "Wedding Officiant": {
    eyebrow: "Wedding Consultation Request",
    title: "Let's plan a ceremony worthy of your love story.",
    lead:
      "Share a few details about your wedding — the more we know, the more thoughtfully Dr. Byers can prepare. Formal or casual, faith-based or civil, beachside or church, elopement or full ceremony — we tailor every officiating experience to you. Response typically within the same business day.",
    backdrop: IMG.weddingHero,
    placeholder:
      "Tell us about your ceremony — date, venue, style, faith tradition, and anything else that will help us prepare.",
  },
  Apostille: {
    eyebrow: "Apostille & International Document Request",
    title: "Authenticate your documents for use anywhere in the world.",
    lead:
      "Share the destination country, document type, and timing pressure — we'll respond with a written quote, expected turnaround, and whether your document qualifies for standard Hague Apostille or requires chain authentication through the U.S. Department of State and the destination consulate.",
    backdrop: IMG.apostilleHero,
    placeholder:
      "Tell us the destination country, document type (birth certificate, POA, corporate formation, diploma, etc.), and any deadline you're working with.",
  },
  "Elder Care Notary": {
    eyebrow: "Bedside Notary Request",
    title: "Compassionate, dignified notary service — right at the bedside.",
    lead:
      "Whether it's a hospital, hospice, rehab center, assisted-living community, or private residence, we arrive prepared, respectful, and coordinated with facility staff. Powers of attorney, healthcare surrogate designations, living wills, and urgent documents handled with the utmost care and full Florida-compliant verification.",
    backdrop: IMG.eldercareHero,
    placeholder:
      "Tell us the facility name and location, the signer's condition and awareness, the document type, and any timing urgency (same-day, evening, weekend).",
  },
  "Cruise-Port Signings": {
    eyebrow: "Cruise-Port Signing Request",
    title: "Notary service before you sail — Port Canaveral, on your schedule.",
    lead:
      "Boarding a cruise from Port Canaveral and need a document notarized before you leave the dock? OGO Notary meets embarking passengers at nearby hotels, terminals, or pier-side staging areas for last-minute powers of attorney, minor-travel consents, medical authorizations, and vessel/charter paperwork. Same-day and pre-dawn appointments available around every major sailing.",
    backdrop: IMG.spaceCoastHero,
    placeholder:
      "Tell us your sailing date/time, cruise line & terminal (if known), the document(s) needing notarization, and where you'd like to meet (hotel, port, home).",
  },
};

const DEFAULT_HERO = {
  eyebrow: "Contact",
  title: "Schedule a signing. Ask a question. Add us to your vendor list.",
  lead:
    "Use the form below — it's the fastest way to reach us. We respond to every inquiry, typically the same business day. For urgent or after-hours appointments, please note that in your message.",
  backdrop: IMG.notaryStamp,
  placeholder:
    "Tell us about the document, location, and any timing constraints.",
};

const inputBase =
  "w-full bg-[#1A2B45] border border-white/10 text-white p-4 text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none transition-colors rounded-none placeholder:text-slate-500";

export default function ContactPage() {
  const { search } = useLocation();

  // Pre-select service via query string, e.g. /contact?service=Wedding+Officiant
  const initialService = React.useMemo(() => {
    const params = new URLSearchParams(search);
    const svc = params.get("service");
    return svc && SERVICE_VALUES.has(svc) ? svc : "";
  }, [search]);

  const [form, setForm] = React.useState({
    full_name: "",
    email: "",
    phone: "",
    service: initialService,
    preferred_date: "",
    preferred_time: "",
    message: "",
  });

  // If URL changes to a different service (e.g. user navigates), sync it
  React.useEffect(() => {
    if (initialService && !form.service) {
      setForm((f) => ({ ...f, service: initialService }));
    }
  }, [initialService, form.service]);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (
      !form.full_name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.service ||
      !form.message.trim()
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const preferred_datetime = [form.preferred_date, form.preferred_time]
        .filter(Boolean)
        .join(" ");
      await axios.post(`${API}/contact`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service,
        preferred_datetime: preferred_datetime || null,
        message: form.message.trim(),
      });
      setSuccess(true);
      setForm({
        full_name: "",
        email: "",
        phone: "",
        service: "",
        preferred_date: "",
        preferred_time: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't submit your request. Please try again or email us directly.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const heroConfig = HERO_VARIANTS[form.service] || DEFAULT_HERO;

  return (
    <>
      <PageHero
        eyebrow={heroConfig.eyebrow}
        title={heroConfig.title}
        lead={heroConfig.lead}
        backdrop={heroConfig.backdrop}
        backdropOpacity={0.6}
        bright
      />

      <section className="relative overflow-hidden pb-24 sm:pb-32 pt-12">
        <FadedBackdrop src={IMG.signingTable} opacity={0.2} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-8">
            <form
              data-testid={CONTACT.form}
              onSubmit={onSubmit}
              className="border border-white/10 bg-[#1A2B45] p-8 sm:p-12"
              noValidate
            >
              <SectionEyebrow className="mb-3">
                Appointment Request
              </SectionEyebrow>
              <h2 className="font-serif-display text-3xl text-white leading-tight mb-8">
                Send us your details and we'll confirm your appointment.
              </h2>

              {success && (
                <div
                  data-testid={CONTACT.successAlert}
                  className="mb-6 flex items-start gap-3 border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-100">
                    Thanks — your request has been received. We'll be in touch
                    shortly to confirm your appointment.
                  </div>
                </div>
              )}

              {error && (
                <div
                  data-testid={CONTACT.errorAlert}
                  className="mb-6 flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-100">{error}</div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <input
                    data-testid={CONTACT.fullName}
                    type="text"
                    value={form.full_name}
                    onChange={onChange("full_name")}
                    className={inputBase}
                    placeholder="Jane A. Smith"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid={CONTACT.email}
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    className={inputBase}
                    placeholder="you@example.com"
                    required
                  />
                </Field>
                <Field label="Phone Number" required>
                  <input
                    data-testid={CONTACT.phone}
                    type="tel"
                    value={form.phone}
                    onChange={onChange("phone")}
                    className={inputBase}
                    placeholder="(555) 555-0123"
                    required
                  />
                </Field>
                <Field label="Service Needed" required>
                  <Select
                    value={form.service}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, service: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid={CONTACT.serviceTrigger}
                      className="bg-[#1A2B45] border-white/10 text-white p-4 h-auto text-sm rounded-none focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] [&>span]:text-left"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid={CONTACT.service}
                      className="bg-[#1A2B45] border border-white/10 rounded-none text-white"
                    >
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          data-testid={`contact-service-${s.value
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                          className="rounded-none focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Preferred Date">
                  <input
                    data-testid={CONTACT.preferredDate}
                    type="date"
                    value={form.preferred_date}
                    onChange={onChange("preferred_date")}
                    className={inputBase}
                  />
                </Field>
                <Field label="Preferred Time">
                  <input
                    data-testid={CONTACT.preferredTime}
                    type="time"
                    value={form.preferred_time}
                    onChange={onChange("preferred_time")}
                    className={inputBase}
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Message" required>
                  <textarea
                    data-testid={CONTACT.message}
                    value={form.message}
                    onChange={onChange("message")}
                    className={`${inputBase} min-h-[140px] resize-y`}
                    placeholder={heroConfig.placeholder}
                    required
                  />
                </Field>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-slate-500 max-w-md leading-relaxed">
                  By submitting this form, you agree to be contacted by OGO
                  Notary about your appointment. We never sell your information.
                </p>
                <GoldButton
                  type="submit"
                  data-testid={CONTACT.submit}
                  disabled={submitting}
                  className={submitting ? "opacity-60 cursor-not-allowed" : ""}
                >
                  {submitting ? "Sending…" : "Send Request"}
                  {!submitting && <ArrowUpRight className="w-4 h-4" />}
                </GoldButton>
              </div>
            </form>
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4">
            <div className="border border-white/10 bg-[#1A2B45] p-8">
              <SectionEyebrow className="mb-3">Direct Contact</SectionEyebrow>
              <h3 className="font-serif-display text-2xl text-white mb-6">
                OGO Notary
              </h3>
              <ul className="space-y-5">
                <SidebarItem
                  icon={Phone}
                  label="Phone"
                  value="(855) 505-4722"
                  href="tel:+18555054722"
                  sub="Calls answered during business hours."
                />
                <SidebarItem
                  icon={Mail}
                  label="Email"
                  value="Greg@ogonotary.com"
                  href="mailto:Greg@ogonotary.com"
                  sub="Replies typically same business day."
                />
                <SidebarItem
                  icon={MapPin}
                  label="Service Area"
                  value="Brevard County, FL · National / International"
                  sub="Mobile in Brevard · RON & Apostille worldwide."
                />
                <SidebarItem
                  icon={Clock}
                  label="Hours"
                  value="Mon–Sun · By Appointment"
                  sub="Nights & weekends available."
                />
              </ul>
            </div>

            <div className="mt-6 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6">
              <div className="eyebrow mb-2">A Note on Legal Advice</div>
              <p className="text-slate-300 text-sm leading-relaxed">
                A Notary Public is not an attorney and cannot give legal
                advice or draft documents. Please bring completed documents
                and consult a licensed attorney for legal guidance.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.22em] text-slate-400 mb-2">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function SidebarItem({ icon: Icon, label, value, sub, href }) {
  const Value = href ? (
    <a href={href} className="text-white text-base font-medium hover:text-[#D4AF37] transition-colors">
      {value}
    </a>
  ) : (
    <div className="text-white text-base font-medium">{value}</div>
  );
  return (
    <li className="flex items-start gap-4">
      <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-400 mb-1">
          {label}
        </div>
        {Value}
        {sub && <div className="text-slate-500 text-xs mt-1">{sub}</div>}
      </div>
    </li>
  );
}
